import { inventoryItemSchema, type Inventory, type Page } from "@cafegestor/shared";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  Input,
  PageTitle,
  Select,
  Spinner,
  Table,
} from "@/components/ui";
import { useCan } from "@/app/me";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { ProgressSteps } from "@/components/patterns";
import { apiFetch, errorMessage } from "@/lib/api";
import { Pager, SaveError } from "../cadastros/widgets";
import { dateBR, qty, today, unitsOf, useDocAction, useProductDetail, useWarehouses } from "./lib";
import {
  DocStatusBadge,
  LotSelect,
  ProductPicker,
  ReverseAction,
  WarehouseSelect,
} from "./pickers";

export function InventariosPage() {
  const can = useCan();
  const canDraft = can("estoque.inventario.confirmar") || can("estoque.ajuste.lancar");
  const [open, setOpen] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [offset, setOffset] = useState(0);
  const list = useQuery({
    queryKey: ["estoque", "inventarios", offset],
    queryFn: () => apiFetch<Page<Inventory>>(`/estoque/inventarios?limit=50&offset=${offset}`),
    placeholderData: (p) => p,
  });

  return (
    <div className="space-y-6">
      <PageTitle
        title="Inventários"
        description="Conte o estoque; ao confirmar, o sistema lança só a diferença entre o contado e o saldo do sistema naquele momento."
        actions={
          canDraft && (
            <Button onClick={() => setCreating(true)} disabled={creating}>
              Novo inventário
            </Button>
          )
        }
      />
      {creating && (
        <NewInventory
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setCreating(false);
            setOpen(id);
          }}
        />
      )}
      {open && <InventoryEditor id={open} onClose={() => setOpen(null)} />}
      <Card>
        {list.isPending ? (
          <Spinner />
        ) : (
          <>
            <Table head={["Número", "Data", "Depósito", "Itens", "Situação", ""]}>
              {list.data?.items.map((i) => (
                <tr key={i.id}>
                  <td className="px-3 py-2 font-mono">{i.number}</td>
                  <td className="px-3 py-2">{dateBR(i.countedOn)}</td>
                  <td className="px-3 py-2">{i.warehouseName}</td>
                  <td className="px-3 py-2">{i.itemCount}</td>
                  <td className="px-3 py-2">
                    <DocStatusBadge status={i.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => setOpen(i.id)}>
                      Abrir
                    </Button>
                  </td>
                </tr>
              ))}
            </Table>
            <Pager total={list.data?.total ?? 0} offset={offset} onOffset={setOffset} />
          </>
        )}
      </Card>
    </div>
  );
}

function NewInventory({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const whs = useWarehouses();
  const [warehouseId, setWarehouseId] = useState("");
  const [countedOn, setCountedOn] = useState(today());
  const [loadBalances, setLoadBalances] = useState(true);
  const wh = warehouseId || whs.data?.find((w) => w.isDefault)?.id || "";
  const create = useDocAction<unknown, Inventory>({
    path: () => "/estoque/inventarios",
    invalidate: [["estoque", "inventarios"]],
    onDone: (r) => onCreated(r.id),
  });
  return (
    <Card title="Novo inventário">
      <div className="grid gap-4 sm:grid-cols-4 sm:items-end">
        <Field label="Depósito">
          <WarehouseSelect value={wh} onChange={setWarehouseId} />
        </Field>
        <Field label="Data da contagem">
          <Input type="date" value={countedOn} onChange={(e) => setCountedOn(e.target.value)} />
        </Field>
        <label className="inline-flex items-center gap-2 pb-2 text-sm sm:col-span-2">
          <input
            type="checkbox"
            className="size-4 accent-primary"
            checked={loadBalances}
            onChange={(e) => setLoadBalances(e.target.checked)}
          />
          Começar com todos os produtos que têm saldo neste depósito
        </label>
        <div className="flex gap-2 sm:col-span-4">
          <Button
            disabled={!wh || create.isPending}
            onClick={() => create.mutate({ warehouseId: wh, countedOn, loadBalances })}
          >
            Abrir contagem
          </Button>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
        </div>
      </div>
      <div className="mt-3">
        <SaveError error={create.error} />
      </div>
    </Card>
  );
}

function InventoryEditor({ id, onClose }: { id: string; onClose: () => void }) {
  const can = useCan();
  const doc = useQuery({
    queryKey: ["estoque", "inventarios", "detalhe", id],
    queryFn: () => apiFetch<Inventory>(`/estoque/inventarios/${id}`),
  });
  const inv = doc.data;
  const draft = inv?.status === "rascunho";
  const canDraft = can("estoque.inventario.confirmar") || can("estoque.ajuste.lancar");
  const invalidate = [["estoque"]];
  const upsert = useDocAction<unknown, Inventory>({
    path: () => `/estoque/inventarios/${id}/itens`,
    method: "PUT",
    invalidate,
  });
  const remove = useDocAction<{ itemId: string }, Inventory>({
    path: (b) => `/estoque/inventarios/${id}/itens/${b.itemId}`,
    method: "DELETE",
    invalidate,
  });
  const confirm = useDocAction<{ version: number }, Inventory>({
    path: () => `/estoque/inventarios/${id}/confirmar`,
    invalidate,
  });
  const cancel = useDocAction<Record<string, never>, Inventory>({
    path: () => `/estoque/inventarios/${id}/cancelar`,
    invalidate,
  });
  const reverse = useDocAction<{ reason: string }, Inventory>({
    path: () => `/estoque/inventarios/${id}/estornar`,
    invalidate,
  });
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [ask, setAsk] = useState<null | "confirmar" | "cancelar">(null);

  if (!inv) return <Spinner />;
  const diffOf = (counted: string, expected: string) => Number(counted) - Number(expected);

  return (
    <Card
      title={`Inventário ${inv.number} — ${inv.warehouseName} (${dateBR(inv.countedOn)})`}
      actions={
        <Button size="sm" variant="secondary" onClick={onClose}>
          Fechar
        </Button>
      }
    >
      <div className="space-y-4 text-sm">
        <div className="flex flex-wrap items-center gap-3">
          <DocStatusBadge status={inv.status} />
          <ProgressSteps
            label="Andamento do inventário"
            steps={["Contagem", "Confirmado (estoque ajustado)"]}
            current={inv.status === "rascunho" ? 0 : inv.status === "confirmado" ? 2 : -1}
          />
        </div>
        {draft && canDraft && (
          <AddCount warehouseId={inv.warehouseId} onAdd={(b) => upsert.mutate(b)} />
        )}
        <Table head={["Produto", "Lote", "Contado", "Sistema", "Diferença", ""]}>
          {inv.items?.map((i) => {
            const diff =
              i.differenceBase !== null
                ? Number(i.differenceBase)
                : diffOf(i.countedBase, i.expectedBase);
            return (
              <tr key={i.id}>
                <td className="px-3 py-2">
                  <span className="font-mono text-xs">{i.productCode}</span> {i.productName}
                </td>
                <td className="px-3 py-2 font-mono">{i.lotCode ?? "—"}</td>
                <td className="text-right tabular-nums px-3 py-2">
                  {draft && canDraft ? (
                    <div className="flex items-center gap-2">
                      <Input
                        className="w-28"
                        inputMode="decimal"
                        value={edits[i.id] ?? qty(i.countedQuantity).replace(/\./g, "")}
                        onChange={(e) => setEdits({ ...edits, [i.id]: e.target.value })}
                        onBlur={() => {
                          const v = edits[i.id];
                          if (v === undefined) return;
                          upsert.mutate({
                            productId: i.productId,
                            unitId: i.unitId,
                            lotId: i.lotId,
                            countedQuantity: v,
                          });
                        }}
                      />
                      <span className="text-xs">{i.unitCode}</span>
                    </div>
                  ) : (
                    qty(i.countedQuantity, i.unitCode)
                  )}
                </td>
                <td className="px-3 py-2 text-right">{qty(i.expectedBase, i.baseUnitCode)}</td>
                <td className="px-3 py-2 text-right">
                  {diff === 0 ? (
                    <Badge>ok</Badge>
                  ) : (
                    <Badge tone={diff > 0 ? "ok" : "error"}>
                      {diff > 0 ? "+" : "−"}
                      {qty(String(Math.abs(diff)), i.baseUnitCode)}
                    </Badge>
                  )}
                </td>
                <td className="px-3 py-2 text-right">
                  {draft && canDraft && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove.mutate({ itemId: i.id })}
                    >
                      Remover
                    </Button>
                  )}
                </td>
              </tr>
            );
          })}
        </Table>
        {(upsert.error || remove.error) && (
          <Alert tone="error">{errorMessage(upsert.error ?? remove.error)}</Alert>
        )}
        {draft && (
          <div className="flex flex-wrap gap-2">
            {can("estoque.inventario.confirmar") && (
              <Button
                disabled={confirm.isPending || (inv.items?.length ?? 0) === 0}
                onClick={() => setAsk("confirmar")}
              >
                Confirmar inventário
              </Button>
            )}
            {canDraft && (
              <Button variant="secondary" onClick={() => setAsk("cancelar")}>
                Cancelar inventário
              </Button>
            )}
          </div>
        )}
        <ConfirmDialog
          open={ask === "confirmar"}
          title={`Confirmar o inventário ${inv.number}?`}
          confirmLabel="Confirmar inventário"
          busy={confirm.isPending}
          onCancel={() => setAsk(null)}
          onConfirm={() => {
            setAsk(null);
            confirm.mutate({ version: inv.version });
          }}
        >
          O estoque de {inv.warehouseName} passa a ser o contado: cada diferença vira um movimento
          de ajuste. Depois de confirmado, só o estorno desfaz.
        </ConfirmDialog>
        <ConfirmDialog
          open={ask === "cancelar"}
          title={`Cancelar o inventário ${inv.number}?`}
          confirmLabel="Cancelar inventário"
          destructive
          busy={cancel.isPending}
          onCancel={() => setAsk(null)}
          onConfirm={() => {
            setAsk(null);
            cancel.mutate({});
          }}
        >
          A contagem é descartada e o estoque não muda.
        </ConfirmDialog>
        <SaveError error={confirm.error ?? cancel.error} />
        {inv.status === "estornado" && (
          <Alert tone="warn">Estornado. Motivo: {inv.reversalReason}</Alert>
        )}
        {inv.status === "confirmado" && can("estoque.inventario.confirmar") && (
          <ReverseAction
            pending={reverse.isPending}
            error={reverse.error}
            onConfirm={(reason) => reverse.mutate({ reason })}
          />
        )}
      </div>
    </Card>
  );
}

function AddCount({ warehouseId, onAdd }: { warehouseId: string; onAdd: (b: unknown) => void }) {
  const [productId, setProductId] = useState("");
  const [label, setLabel] = useState("");
  const [unitId, setUnitId] = useState("");
  const [lotId, setLotId] = useState("");
  const [newLot, setNewLot] = useState("");
  const [quantity, setQuantity] = useState("");
  const [error, setError] = useState<string | null>(null);
  const product = useProductDetail(productId);
  const units = unitsOf(product.data);
  function add() {
    const parsed = inventoryItemSchema.safeParse({
      productId,
      unitId: unitId || units[0]?.id,
      countedQuantity: quantity,
      lotId: newLot ? null : lotId,
      newLot: newLot ? { code: newLot } : null,
    });
    if (!parsed.success) return setError(parsed.error.issues[0]?.message ?? "Dados inválidos");
    setError(null);
    onAdd(parsed.data);
    setProductId("");
    setLabel("");
    setQuantity("");
    setLotId("");
    setNewLot("");
  }
  return (
    <div className="grid gap-3 rounded-md border p-3 sm:grid-cols-12 sm:items-end">
      <div className="sm:col-span-4">
        <Field label="Produto contado">
          <ProductPicker
            key={label === "" ? "empty" : "filled"}
            value={productId}
            label={label}
            onChange={(id, l) => {
              setProductId(id);
              setLabel(l);
              setUnitId("");
            }}
          />
        </Field>
      </div>
      {product.data?.controlsLot && (
        <div className="grid gap-3 sm:col-span-4 sm:grid-cols-2">
          <Field label="Lote">
            <LotSelect
              productId={productId}
              warehouseId={warehouseId}
              onlyWithBalance={false}
              value={lotId}
              onChange={setLotId}
            />
          </Field>
          <Field label="ou lote novo">
            <Input value={newLot} onChange={(e) => setNewLot(e.target.value)} />
          </Field>
        </div>
      )}
      <div className="sm:col-span-2">
        <Field label="Unidade">
          <Select value={unitId || units[0]?.id || ""} onChange={(e) => setUnitId(e.target.value)}>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.code}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="sm:col-span-1">
        <Field label="Qtde">
          <Input
            inputMode="decimal"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </Field>
      </div>
      <div className="sm:col-span-1">
        <Button size="sm" onClick={add} disabled={!productId}>
          Incluir
        </Button>
      </div>
      {error && (
        <div className="sm:col-span-12">
          <Alert tone="error">{error}</Alert>
        </div>
      )}
    </div>
  );
}
